import { useEffect, useState } from "react";
import { BellRing, BellOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionHeading, Reveal } from "@/components/Section";
import SportBadge from "@/components/SportBadge";
import { pushSupported, getExistingSubscription, enablePush, updatePushTeams, disablePush } from "@/lib/push";

export default function ScoreAlerts() {
  const [teams, setTeams] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    api.get("/teams").then(({ data }) => setTeams(data.teams || [])).catch(() => {});
    getExistingSubscription()
      .then(async (sub) => {
        if (!sub) return;
        try {
          const { data } = await api.get(`/push/subscription?endpoint=${encodeURIComponent(sub.endpoint)}`);
          setSelected(new Set(data.team_slugs || []));
          setDone(true);
        } catch { /* fresh start */ }
      })
      .catch(() => {});
  }, []);

  const toggle = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  };

  const enable = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one team");
      return;
    }
    setBusy(true);
    try {
      if (await getExistingSubscription()) await updatePushTeams([...selected]);
      else await enablePush([...selected]);
      setDone(true);
      toast.success("Notifications on — finals will pop up on this device");
    } catch (err) {
      toast.error(err.message || "Couldn't enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      await disablePush();
      setDone(false);
      setSelected(new Set());
      toast.success("Notifications off for this device");
    } catch (err) {
      toast.error(err.message || "Couldn't turn off notifications");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="alerts" className="border-y border-bline bg-surface/40 scroll-mt-24" data-testid="alerts-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12">
        <SectionHeading index="06" eyebrow="NEVER MISS A FINAL" title="Score Alerts" />
        <Reveal>
          <div className="grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm bg-volt/10">
                  <BellRing className="h-6 w-6 text-volt" />
                </span>
                <div>
                  <h3 className="font-display text-2xl tracking-wide text-white">FINALS, STRAIGHT TO YOUR DEVICE</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    Pick your squads and tap enable. The moment any of them go final, a notification pops up on this
                    device — plus a 1-hour heads-up before game time. No email, no account, no spam.
                  </p>
                </div>
              </div>
              {!supported ? (
                <div className="mt-8 flex items-center gap-3 rounded-lg border border-bline bg-surface p-5" data-testid="alerts-unsupported">
                  <Smartphone className="h-6 w-6 shrink-0 text-slate-500" />
                  <p className="text-sm text-slate-400">
                    This browser doesn't support push notifications. On iPhone, install SportsGearDFW to your Home Screen first — then enable alerts here.
                  </p>
                </div>
              ) : done ? (
                <div className="mt-8 space-y-3" data-testid="alerts-success">
                  <div className="flex items-center gap-3 rounded-lg border border-volt/40 bg-volt/5 p-5">
                    <BellRing className="h-6 w-6 shrink-0 text-volt" />
                    <p className="text-sm text-slate-200">
                      Notifications on for <strong className="text-volt">{selected.size}</strong> team{selected.size === 1 ? "" : "s"}. Finals and game-time reminders will pop up on this device.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid="alerts-update-btn"
                      onClick={enable}
                      disabled={busy}
                      className="flex-1 rounded-sm bg-volt py-3 font-display text-lg tracking-widest text-ink transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                      {busy ? "WORKING…" : "UPDATE TEAMS"}
                    </button>
                    <button
                      data-testid="alerts-turnoff-btn"
                      onClick={turnOff}
                      disabled={busy}
                      className="flex items-center gap-2 rounded-sm border border-bline px-4 py-3 font-display text-lg tracking-widest text-slate-300 transition-colors hover:border-liversed hover:text-liversed disabled:opacity-50"
                    >
                      <BellOff className="h-4 w-4" /> TURN OFF
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-8" data-testid="alerts-form">
                  <button
                    data-testid="alerts-enable-btn"
                    onClick={enable}
                    disabled={busy}
                    className="w-full rounded-sm bg-volt py-3 font-display text-xl tracking-widest text-ink transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {busy ? "ENABLING…" : `NOTIFY ME FOR ${selected.size || "…"} TEAM${selected.size === 1 ? "" : "S"}`}
                  </button>
                  <p className="mt-3 font-score text-[10px] leading-relaxed tracking-wider text-slate-500">
                    YOUR BROWSER WILL ASK FOR NOTIFICATION PERMISSION — TAP ALLOW. ALERTS ONLY REACH THIS DEVICE.
                  </p>
                </div>
              )}
            </div>
            <div className="lg:col-span-3">
              <p className="mb-3 font-score text-[10px] tracking-[0.3em] text-slate-500">TAP TO SELECT YOUR TEAMS</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="alerts-team-grid">
                {teams.map((t) => {
                  const on = selected.has(t.slug);
                  return (
                    <button
                      key={t.slug}
                      type="button"
                      data-testid={`alert-chip-${t.slug}`}
                      onClick={() => toggle(t.slug)}
                      className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-all duration-150 ${
                        on ? "border-volt bg-volt/10" : "border-bline bg-surface hover:border-volt/40"
                      }`}
                    >
                      <span className="h-6 w-1 shrink-0 rounded-full" style={{ backgroundColor: t.color }} />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className={`truncate text-xs font-bold ${on ? "text-volt" : "text-white"}`}>{t.name}</span>
                          <SportBadge sportLabel={t.sport_label} testid={`alert-sport-badge-${t.slug}`} />
                        </span>
                        <span className="block font-score text-[9px] tracking-widest text-slate-500">{t.league}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
